<script setup lang="ts">
import { computed, onMounted, ref, shallowRef } from "vue";
import { useRoute, useRouter } from "vue-router";

import SkoreSpinner from "@/components/ui/SkoreSpinner.vue";
import { getReportName, type EstimatorReportDto } from "@/dto";
import DefaultPageLayout from "@/layouts/DefaultPageLayout.vue";
import { RouteNames } from "@/router";
import { useApi } from "@/services/api";
import EstimatorReport from "@/views/EstimatorReport.vue";

const api = useApi();
const route = useRoute();
const router = useRouter();
const report = shallowRef<EstimatorReportDto>();
const isLoading = ref(true);
const tenantName = computed(() => route.params.tenantName as string);
const projectName = computed(() => route.params.projectName as string);
const reportId = computed(() => route.params.reportId as string);

async function fetchExperiment() {
  isLoading.value = true;
  const r = await api.fetchEstimatorReport(
    tenantName.value,
    projectName.value,
    parseInt(reportId.value)
  );
  if (r === null) {
    router.push({ name: RouteNames.NotFound });
  } else {
    report.value = r;
    isLoading.value = false;
  }
}

onMounted(async () => {
  await fetchExperiment();
});
</script>

<template>
  <DefaultPageLayout
    :breadcrumb-items="[
      {
        label: tenantName,
        to: { name: RouteNames.Projects, params: { tenantName } },
        icon: 'icon-masonry',
      },
      {
        label: projectName,
        to: { name: RouteNames.ProjectDetail, params: { tenantName, projectName } },
        icon: 'icon-punch-card',
        iconColor: 'var(--color-background-purple)',
      },
      {
        label: report ? getReportName(report) : '...',
      },
    ]"
  >
    <Transition name="fade" mode="out-in">
      <div v-if="isLoading" class="loading">
        <SkoreSpinner />
      </div>
      <div class="estimator-report-view" v-else-if="report !== undefined">
        <EstimatorReport :report="report" :use-anchor="true">
          <template #preTitle>
            <RouterLink :to="{ name: RouteNames.ProjectDetail, hash: '' }" class="back label-s">
              <i class="icon icon-arrow-left" /> Experimentations
            </RouterLink>
          </template>
        </EstimatorReport>
      </div>
    </Transition>
  </DefaultPageLayout>
</template>

<style scoped>
.loading {
  margin-top: 40dvh;
  text-align: center;
}

.back {
  color: var(--color-text-secondary);
  text-decoration: none;

  i {
    padding-right: var(--spacing-8);
    vertical-align: middle;
  }
}
</style>
